import type { AxiosRequestConfig } from "axios"
import { isAxiosError } from "axios"

import {
  API_BASE_URL,
  AXIOS_INSTANCE,
  handleOrganizationRejected,
  isOrganizationRejection,
} from "openapi/mutator/custom-client"
import type {
  AgentAttachment as GeneratedAgentAttachment,
  AgentAttachmentPresignResponse as GeneratedAgentAttachmentPresignResponse,
  AgentMutationReceipt as GeneratedAgentMutationReceipt,
} from "openapi/api/model"
import { parseSseFrame } from "./agent-sse"

export type AgentAskKind = "candidate" | "job" | "other"

export type AgentConversationSummary = {
  id: string
  created_by: string
  organization_id: string
  title: string
  is_archived?: boolean
  created_at: string
  updated_at: string
}

export type AgentConversationMessage = {
  id: string
  conversation_id: string
  author_type: "user" | "bot"
  user_id: string | null
  user_name: string | null
  body: string
  attachments?: AgentConversationAttachment[]
  created_at: string
  updated_at: string
  mutationReceipt?: AgentMutationReceipt
}

export type AgentAskOption = {
  index: number
  label: string
  kind: AgentAskKind
}

export type PendingAgentAsk = {
  conversationId: string
  askUserToolCallId: string
  botMessageId: string
  kind: AgentAskKind
  options: AgentAskOption[]
  createdAt: string
}

export type AgentNarrationResponse =
  | { type: "message" }
  | {
      type: "ask_user"
      askUserToolCallId: string
      kind: AgentAskKind
      options: AgentAskOption[]
    }

export type AgentConversationTurn =
  | { kind: "plain_text"; body: string }
  | {
      kind: "ask_user_response"
      askUserToolCallId: string
      response:
        | { selectedIndex: number }
        | { selectedText: string }
        | { body: string }
    }

export type AgentStreamProgressEvent = {
  type: "progress"
  phase: "intake" | "routing" | "planning" | "tool" | "narration"
  label: string
}

export type AgentMutationReceipt = GeneratedAgentMutationReceipt
export type AgentConversationAttachment = GeneratedAgentAttachment
export type AgentConversationAttachmentPresignResponse =
  GeneratedAgentAttachmentPresignResponse

export type AgentSendConversationMessageResponse = {
  status: "processed"
  conversationId: string
  userMessage: AgentConversationMessage
  botMessage: AgentConversationMessage
  response: AgentNarrationResponse
  mutationReceipt?: AgentMutationReceipt
}

export class AgentApiError extends Error {
  constructor(message: string, public status?: number, public body?: unknown) {
    super(message)
    this.name = "AgentApiError"
  }
}

type AgentListConversationsResponse = {
  conversations: AgentConversationSummary[]
}

type AgentCreateConversationResponse = {
  conversation: AgentConversationSummary
}

type AgentGetConversationResponse = {
  conversation: AgentConversationSummary
  messages: AgentConversationMessage[]
}

type AgentListPendingAsksResponse = {
  pendingAsks: PendingAgentAsk[]
}

function organizationHeaders(): Record<string, string> {
  try {
    const orgId = localStorage.getItem("currentOrganization")
    return orgId ? { "x-organization-id": orgId } : {}
  } catch {
    return {}
  }
}

async function readResponseBody(response: Response): Promise<unknown> {
  const text = await response.text().catch(() => "")
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

async function agentJson<T>(config: AxiosRequestConfig): Promise<T> {
  try {
    const { data } = await AXIOS_INSTANCE.request<T>(config)
    return data
  } catch (error) {
    if (isAxiosError(error)) {
      throw new AgentApiError(
        error.message,
        error.response?.status,
        error.response?.data,
      )
    }
    throw error instanceof AgentApiError
      ? error
      : new AgentApiError(
          error instanceof Error ? error.message : "Agent request failed.",
        )
  }
}

function agentPath(path: string): string {
  return `${API_BASE_URL.replace(/\/+$/, "")}${path}`
}

export function listAgentConversations(signal?: AbortSignal) {
  return agentJson<AgentListConversationsResponse>({
    url: "/agents/conversations",
    method: "GET",
    signal,
  })
}

export function createAgentConversation(
  body?: { title?: string },
  signal?: AbortSignal,
) {
  return agentJson<AgentCreateConversationResponse>({
    url: "/agents/conversations",
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: body,
    signal,
  })
}

export function getAgentConversation(
  conversationId: string,
  signal?: AbortSignal,
) {
  return agentJson<AgentGetConversationResponse>({
    url: `/agents/conversations/${conversationId}`,
    method: "GET",
    signal,
  })
}

export function presignAgentConversationAttachment(
  conversationId: string,
  attachmentId: string,
  signal?: AbortSignal,
) {
  return agentJson<AgentConversationAttachmentPresignResponse>({
    url: `/agents/conversations/${conversationId}/attachments/${attachmentId}/presign`,
    method: "GET",
    signal,
  })
}

export async function listPendingAgentAsks(
  conversationId: string,
  signal?: AbortSignal,
): Promise<AgentListPendingAsksResponse> {
  const response = await agentJson<Omit<AgentListPendingAsksResponse, "pendingAsks"> & {
    pendingAsks: Array<Omit<PendingAgentAsk, "conversationId">>
  }>({
    url: `/agents/conversations/${conversationId}/pending-asks`,
    method: "GET",
    signal,
  })

  return {
    pendingAsks: response.pendingAsks.map((ask) => ({
      ...ask,
      conversationId,
    })),
  }
}

export async function streamAgentConversationMessage(
  conversationId: string,
  turn: AgentConversationTurn,
  options: {
    files?: File[]
    onProgress?: (event: AgentStreamProgressEvent) => void
    signal?: AbortSignal
  } = {},
): Promise<AgentSendConversationMessageResponse> {
  const files = options.files?.filter((file) => file.size > 0) ?? []
  if (files.length && turn.kind !== "plain_text") {
    throw new AgentApiError(
      "Agent attachments can only be sent with plain text messages.",
    )
  }

  const headers: Record<string, string> = {
    Accept: "text/event-stream",
    ...organizationHeaders(),
  }
  let body: BodyInit
  if (files.length) {
    const formData = new FormData()
    formData.append("kind", "plain_text")
    formData.append("body", turn.kind === "plain_text" ? turn.body : "")
    for (const file of files) {
      formData.append("files", file)
    }
    body = formData
  } else {
    headers["Content-Type"] = "application/json"
    body = JSON.stringify(turn)
  }

  const response = await fetch(
    agentPath(`/agents/conversations/${conversationId}/messages/stream`),
    {
      method: "POST",
      credentials: "include",
      headers,
      body,
      signal: options.signal,
    },
  )

  if (!response.ok) {
    const errorBody = await readResponseBody(response)
    // Raw fetch bypasses the Axios interceptor.
    if (isOrganizationRejection(response.status, errorBody)) handleOrganizationRejected()
    throw new AgentApiError("Agent stream request failed.", response.status, errorBody)
  }
  if (!response.body) {
    throw new AgentApiError("Agent stream response was empty.", response.status)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""

  const handleFrame = (rawFrame: string): AgentSendConversationMessageResponse | null => {
    const frame = parseSseFrame(rawFrame)
    if (!frame) return null

    if (frame.event === "progress") {
      options.onProgress?.(frame.data as AgentStreamProgressEvent)
      return null
    }
    if (frame.event === "final") {
      return frame.data as AgentSendConversationMessageResponse
    }
    if (frame.event === "error") {
      throw new AgentApiError("Agent stream failed.", response.status, frame.data)
    }
    return null
  }

  while (true) {
    const { done, value } = await reader.read()
    buffer += decoder.decode(value, { stream: !done })

    const frames = buffer.split(/\r?\n\r?\n/)
    buffer = frames.pop() ?? ""

    for (const rawFrame of frames) {
      const final = handleFrame(rawFrame)
      if (final) return final
    }

    if (done) break
  }

  if (buffer.trim()) {
    const final = handleFrame(buffer)
    if (final) return final
  }

  throw new AgentApiError("Agent stream ended before final event.", response.status)
}
